import { QueryClient } from '@tanstack/react-query'
import { ApiError, NetworkError } from './problem'

/**
 * TanStack Query 的全局配置（AGENTS.md 9：服务端状态用现成方案，不自己写缓存）。
 *
 * 默认值改了三处，每一处都对着后端契约：
 *
 * 1. **不重试 4xx。** 默认会重试 3 次，但 `401 需要登录`、`404 已下架`、
 *    `422 参数不合法` 重试多少次都是同一个结果，只是白打三次请求。只有网络
 *    错误和 5xx 才值得重试。
 * 2. **`staleTime` 给 30 秒。** 后端的列表接口本身就是弱一致的
 *    （`pagination_note` 明说 `as_of` 不是数据库快照），所以半分钟内复用缓存
 *    不会引入契约之外的不一致，却能省掉切页来回的重复请求。
 * 3. **`refetchOnWindowFocus` 关掉。** 这是个资源站，用户切回标签页时不需要
 *    整页重取；真正需要新鲜数据的地方（下载会话剩余时间）自己有刷新逻辑。
 */

/** 4xx 里唯一值得重试的是 429，它带 `Retry-After`，但交由调用方处理更合适。 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false
  if (error instanceof NetworkError) return true
  if (error instanceof ApiError) return error.status >= 500
  return false
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        // cursor 翻页时保留上一页数据，避免整页闪成骨架屏。
        placeholderData: <T,>(previous: T) => previous,
      },
      mutations: {
        // 写操作绝不自动重试：重复提交是 bug，不是恢复手段。
        // 下载会话的重放安全由 `Idempotency-Key` 保证，而不是靠重试。
        retry: false,
      },
    },
  })
}

/**
 * 查询键。
 *
 * 集中定义而不是在各处手写数组，是为了让失效（invalidate）能精确命中：
 * 收藏成功后要让 `posts.detail(slug)` 和 `favorites.list()` 都失效，
 * 键散落在组件里的话，迟早会漏掉一个然后出现「收藏了但列表没变」。
 */
export const queryKeys = {
  currentUser: ['me'] as const,
  posts: {
    all: ['posts'] as const,
    search: (params: Record<string, unknown>) => ['posts', 'search', params] as const,
    detail: (slug: string) => ['posts', 'detail', slug] as const,
  },
  creators: {
    all: ['creators'] as const,
    list: (q: string | undefined) => ['creators', 'list', q ?? ''] as const,
    detail: (publicId: string) => ['creators', 'detail', publicId] as const,
    posts: (publicId: string, params: Record<string, unknown>) =>
      ['creators', 'posts', publicId, params] as const,
  },
  tags: {
    all: ['tags'] as const,
    list: (params: Record<string, unknown>) => ['tags', 'list', params] as const,
    /** 「标签大全」的一页（`GET /tags/catalog`）。与 `list` 不是同一条路由。 */
    catalog: (params: Record<string, unknown>) => ['tags', 'catalog', params] as const,
    /** 单个标签（`GET /tags/{public_id}`）。标签落地页的标题靠它。 */
    detail: (publicId: string) => ['tags', 'detail', publicId] as const,
    posts: (publicId: string, params: Record<string, unknown>) =>
      ['tags', 'posts', publicId, params] as const,
    /**
     * 首页展示位（公开侧）。
     *
     * 刻意挂在 `['tags', …]` 下面：改标签名会立刻改变磁贴上的文字，而站内已有
     * 好几处「改完标签就 `invalidateQueries(queryKeys.tags.all)`」——放在别的前缀
     * 下就得在每一处再补一句，漏一处的表现是「标签改名了，首页那格还是旧名字」。
     */
    featured: (params: Record<string, unknown>) => ['tags', 'featured', params] as const,
  },
  favorites: {
    all: ['favorites'] as const,
    list: (params: Record<string, unknown>) => ['favorites', 'list', params] as const,
  },
  /**
   * 管理端。
   *
   * 全部挂在 `['admin', …]` 下面，这样退出管理后台（或登出）时一句
   * `invalidateQueries({ queryKey: queryKeys.admin.all })` 就能清干净，
   * 不会有某个管理端列表残留在缓存里被公开页面读到。
   */
  admin: {
    all: ['admin'] as const,
    users: (params: Record<string, unknown>) => ['admin', 'users', params] as const,
    user: (id: string) => ['admin', 'user', id] as const,
    /*
      细分权限。清单是全站唯一一份、不随账号变化，所以它没有参数；
      某个人的授权状态用单数键，与 `user(id)` 分开 —— 它们的 ETag 是同一个
      `row_version`，但改角色只该失效前者、授权只该失效两者，
      混成一个键就没法表达这个区别。
    */
    permissionCatalog: ['admin', 'permissions'] as const,
    userPermissions: (id: string) => ['admin', 'user-permissions', id] as const,
    auditLogs: (params: Record<string, unknown>) => ['admin', 'audit-logs', params] as const,
    creator: (id: string) => ['admin', 'creator', id] as const,
    /*
      两条列表键的前缀是复数（`['admin','tags',…]`），单条是单数
      （`['admin','tag',id]`）—— 写完之后用复数前缀一次失效掉所有筛选组合与页码，
      而单数键不会被那次失效顺带清掉，因为它不是复数键的前缀。
    */
    creators: (params: Record<string, unknown>) => ['admin', 'creators', params] as const,
    tags: (params: Record<string, unknown>) => ['admin', 'tags', params] as const,
    tag: (id: string) => ['admin', 'tag', id] as const,
    /** 展示位列表不分页也没有参数：后端返回完整的一份。 */
    featuredTags: ['admin', 'featured-tags'] as const,
    featuredTag: (id: string) => ['admin', 'featured-tag', id] as const,
    posts: (params: Record<string, unknown>) => ['admin', 'posts', params] as const,
    post: (id: string) => ['admin', 'post', id] as const,
    draftPreview: (id: string) => ['admin', 'draft-preview', id] as const,
    publicationAttempt: (id: string) => ['admin', 'publication-attempt', id] as const,
    mediaAsset: (id: string) => ['admin', 'media-asset', id] as const,
    /** 遮罩库不分页也没有参数：后端返回完整的一份。 */
    imageOverlays: ['admin', 'image-overlays'] as const,
    release: (id: string) => ['admin', 'release', id] as const,
    uploadSession: (id: string) => ['admin', 'upload-session', id] as const,
    storageRetention: ['admin', 'storage-retention'] as const,
    outbox: (params: Record<string, unknown>) => ['admin', 'outbox', params] as const,
    deletionBatch: (id: string) => ['admin', 'deletion-batch', id] as const,
    dau: (params: Record<string, unknown>) => ['admin', 'dau', params] as const,
    mau: (params: Record<string, unknown>) => ['admin', 'mau', params] as const,
    dailyPageViews: (params: Record<string, unknown>) =>
      ['admin', 'page-views', 'daily', params] as const,
    monthlyPageViews: (params: Record<string, unknown>) =>
      ['admin', 'page-views', 'monthly', params] as const,
  },
} as const
